import {
  Brain,
  ClipboardList,
  Activity,
  Clock,
  Gauge,
  Target,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { clinicIdentity } from '@/lib/clinic-identity'

// ----------
// Types
// ----------
export interface Symptom {
  label: string
  icon: LucideIcon
}

export interface ProcessStep {
  step: number
  title: string
  detail: string
}

export interface Modality {
  title: string
  points: string[]
}

export interface Session {
  title: string
  subtitle?: string
  bullets: string[]
}

export interface FAQ {
  question: string
  answer: string
}

export interface ProblemArea {
  key: string
  title: string
  icon: LucideIcon
  items: string[]
}

// ----------
// Constants
// ----------
export const ASSESSMENT_URL = '/adhd-autism-assessment-tool'
export const BOOKING_URL = '/book'
export const BUSINESS_NAME = clinicIdentity.name
export const PAGE_URL = '/attention-deficit-hyperactivity-disorder-therapy'

// ----------
// Data - page refs from the original PDF noted in comments
// ----------

/** Core ADHD symptoms shown in the hero - p1 */
export const symptoms: Symptom[] = [
  { label: 'Inattention', icon: Target },
  { label: 'Disorganization & forgetfulness', icon: ClipboardList },
  { label: 'Impulsivity / hyperactivity', icon: Activity },
  { label: 'Emotional difficulties', icon: Brain },
  { label: 'Procrastination', icon: Clock },
  { label: 'Poor time management', icon: Gauge },
]

/** Impact areas - p1 */
export const impacts: string[] = [
  'At school',
  'At work',
  'In daily life',
  'Relationships',
]

/** Problem breakdowns per area - p1‑2 */
export const problemAreas: ProblemArea[] = [
  {
    key: 'school',
    title: 'At school',
    icon: ClipboardList,
    items: [
      'Disturbing classes',
      'Difficulty sustaining attention in classes',
      'May have had disciplinary issues',
      'Difficulty maintaining consistent grades',
    ],
  },
  {
    key: 'work',
    title: 'At work',
    icon: Activity,
    items: [
      'Frequent switching of jobs',
      'Difficulty managing/maintaining a schedule',
      'Difficulties with consistent performance',
      'Difficulty prioritising and completing tasks',
    ],
  },
  {
    key: 'life',
    title: 'In daily life',
    icon: Clock,
    items: [
      'Difficulty completing chores',
      'Difficulties in managing day‑to‑day tasks',
      'Difficulties in regulating mood',
      'More risk‑taking behaviours (e.g., reckless driving, financial difficulties)',
    ],
  },
  {
    key: 'relationships',
    title: 'Relationships',
    icon: Brain,
    items: [
      'Difficulties in sustaining and maintaining relationships',
      'Difficulties with communication',
      'Inconsistent responses to calls/messages',
      'Forgetting set plans with friends/partners/family',
    ],
  },
]

/** 5‑step care pathway - p3 */
export const processSteps: ProcessStep[] = [
  {
    step: 1,
    title: 'Consultation with psychiatrist',
    detail:
      'Initial diagnostic evaluation, medical history and discussion of goals.',
  },
  {
    step: 2,
    title: 'Assessments',
    detail:
      'Adult ADHD Self‑Rating Scale (ASRS) + CNSVS - Central Nervous System Vital Signs + screening for co‑morbid mood issues.',
  },
  {
    step: 3,
    title: 'Psychology intake',
    detail:
      'Full psychosocial history with the psychologist to complete the picture.',
  },
  {
    step: 4,
    title: 'Personalized treatment plan',
    detail: 'Plan shared after psychiatrist + psychologist inputs.',
  },
  {
    step: 5,
    title: 'Begin therapy + reviews',
    detail:
      'Start sessions with psychologist; psychiatrist follow‑ups as needed.',
  },
]

/** Treatment modalities - p4‑5 */
export const modalities: Modality[] = [
  {
    title: 'Medication',
    points: [
      'When indicated, evidence‑based pharmacotherapy as part of a combined plan.',
    ],
  },
  {
    title: 'ADHD Coaching',
    points: [
      'Organization',
      'Procrastination',
      'Time management',
      'Self‑regulation',
      'Attention & impulsivity',
    ],
  },
  {
    title: 'Psychotherapy (CBT‑focused)',
    points: [
      'Cognitive restructuring',
      'Relaxation / mindfulness skills',
      'Exposure & social‑skills training',
      'Interpersonal therapy elements as needed',
    ],
  },
]

/** 15‑session plan - p6‑7, 9‑10 */
export const sessions: Session[] = [
  {
    title: 'Sessions 1–2: Organization & Goal‑setting',
    bullets: [
      'Restructure spaces; list‑making; identify distractions',
      'Focus & decision‑making skills',
      'Get organized at work',
    ],
  },
  {
    title: 'Sessions 3–4: Procrastination (short‑term)',
    bullets: [
      'Estimate task time; prioritize',
      'Unstick with small, achievable goals',
      'Balance work vs. recovery',
    ],
  },
  {
    title: 'Sessions 5–6: Prioritization',
    bullets: [
      'Differentiate & sequence tasks',
      'Scheduling techniques for home & work',
      'Stress reduction for irritability / anger',
    ],
  },
  {
    title: 'Sessions 7–8: Self‑regulation (behavioural & emotional)',
    bullets: [
      'Address impulsivity & decision outcomes',
      'Emotional control strategies',
    ],
  },
  {
    title: 'Session 9: Long‑term procrastination',
    bullets: ['Recognize patterns; techniques to prevent long delays'],
  },
  {
    title: 'Session 10: Inattention',
    bullets: ['Techniques to improve attention span'],
  },
  {
    title: 'Sessions 11–12: Time management',
    bullets: [
      'Reduce work‑related anxiety via planning',
      'Motivation systems that stick',
    ],
  },
  {
    title: 'Sessions 13–15: Mood & motivation',
    bullets: [
      'Identify negative thoughts & cognitive distortions',
      'Replace unhealthy coping with healthy patterns',
      'Stress management, relaxation & coping skills',
      'Sustained motivation & accountability',
    ],
  },
]

/** FAQ items */
export const faqItems: FAQ[] = [
  {
    question: 'Is ADHD actually treatable in adults?',
    answer:
      'Yes. Combining therapy (CBT‑based skills & coaching) with medication when indicated is effective, and we tailor the plan to your life and goals.',
  },
  {
    question: 'Are medications vital for treatment?',
    answer:
      'Not necessarily. Medication can help but is not mandatory; we decide together after the initial assessment.',
  },
  {
    question: 'How fast will I see changes?',
    answer:
      'Many people feel wins in the first few weeks as organization and prioritization improve; deeper habits solidify over the full program.',
  },
  {
    question: 'Is this only for work issues?',
    answer:
      'No-skills translate across work, home, relationships and general well‑being.',
  },
]

/** Session format cards */
export interface FormatCard {
  title: string
  description: string
}

export const formatCards: FormatCard[] = [
  {
    title: 'Session length',
    description:
      '50–60 minutes per session; weekly is typical during the core program.',
  },
  {
    title: 'Mode',
    description:
      'In‑clinic sessions at our Bengaluru clinic, or secure online sessions-whatever keeps you most consistent.',
  },
  {
    title: 'Medication reviews',
    description:
      'Psychiatrist follow‑ups are scheduled as needed alongside therapy.',
  },
]
